import type { IxdDlView, PeriodType, TaggedValue } from "@/apis/compare/types";
import { formatCellForDisplay } from "./cell-display";

const PERIOD_TYPE_ORDER: PeriodType[] = [
  "CURRENT",
  "PREVIOUS",
  "BEFORE_PREVIOUS",
];

export interface ContentTaggedValueMatch {
  periodType: PeriodType;
  taggedValue: TaggedValue;
}

/**
 * content에 대응하는 fact 매칭의 단일 기준 — 값 일치 fact, 없으면 유일 후보.
 *
 * 표시 타입(getDlViewContentSchemaType)과 편집 대상(getContentEditInfo)이 반드시
 * 같은 fact를 가리켜야 한다. 기준이 갈라지면 string content가 옆 fact의 duration
 * 타입으로 편집·변환되는 부류의 버그가 재발하므로 양쪽 모두 이 함수만 쓴다.
 */
export const findContentTaggedValue = (
  values: IxdDlView["values"],
  contentText: string,
): ContentTaggedValueMatch | null => {
  if (!values) return null;

  const candidates: ContentTaggedValueMatch[] = [];
  for (const periodType of PERIOD_TYPE_ORDER) {
    const taggedValues = values[periodType] ?? [];
    const exactMatch = taggedValues.find(
      (taggedValue) => String(taggedValue.value) === contentText,
    );
    if (exactMatch) return { periodType, taggedValue: exactMatch };
    candidates.push(
      ...taggedValues.map((taggedValue) => ({ periodType, taggedValue })),
    );
  }

  return candidates.length === 1 ? candidates[0] : null;
};

export const getDlViewContentSchemaType = (
  dlView: Pick<IxdDlView, "schemaType" | "values" | "content">,
): string | null => {
  if (dlView.schemaType?.trim()) return dlView.schemaType;
  const matched = findContentTaggedValue(
    dlView.values,
    getDlViewContentText(dlView),
  );
  return matched?.taggedValue.metadata.schemaType.trim() || null;
};

export const hasDlViewContent = (dlView: Pick<IxdDlView, "content">): boolean =>
  dlView.content !== null && dlView.content !== undefined;

export const getDlViewContentText = (
  dlView: Pick<IxdDlView, "content">,
): string => (hasDlViewContent(dlView) ? String(dlView.content) : "");

export const formatDlViewContentForDisplay = (
  dlView: Pick<IxdDlView, "content" | "schemaType" | "unitText" | "values">,
): string => {
  const { displayText } = formatCellForDisplay({
    rawText: getDlViewContentText(dlView),
    schemaType: getDlViewContentSchemaType(dlView),
    unitText: dlView.unitText,
  });

  return displayText;
};
